import { useEffect, useRef } from 'react'
import ReactGA from 'react-ga4'
import { roles, roleSlugToLabel } from '../../data/roles'
import type { Categories, FiltersProps } from '../../types/portfolio'

export default function Filters({
  selected,
  onToggle,
  onVisible,
  genres,
}: FiltersProps) {
  const sectionRef = useRef<HTMLTableRowElement | null>(null)

  const genreProjects: Categories = {
    label: 'Genre',
    categories: genres,
  }

  const groups: Categories[] = [genreProjects, ...roles]

  useEffect(() => {
    if (!onVisible || !sectionRef.current) return

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          onVisible()
          observer.disconnect()
        }
      },
      { threshold: 0.2 }
    )

    observer.observe(sectionRef.current)
    return () => {
      observer.disconnect()
    }
  }, [onVisible])
  return (
    <div className="mb-6">
      <div className="mb-3 flex items-baseline gap-4">
        <span className="text-sm font-semibold uppercase tracking-wide text-neutral-500 dark:text-neutral-200">
          Filters
        </span>
        <span className="text-sm text-neutral-500 dark:text-neutral-300">
          Select one or more
        </span>
      </div>
      <table className="w-fit table-auto">
        <thead className="sr-only">
          <tr>
            <th>Category</th>
            <th>Options</th>
          </tr>
        </thead>
        <tbody>
          {groups.map((categoryGroup: Categories) => (
            <tr key={categoryGroup.label} ref={sectionRef}>
              <td className="flex items-center py-2 pr-4 align-top">
                <span className="text-sm font-medium text-neutral-700 dark:text-neutral-300">
                  {categoryGroup.label}
                </span>
              </td>
              <td className="py-2">
                <ul className="flex list-none flex-wrap gap-1 p-0">
                  {categoryGroup.categories.map((category: string) => {
                    const value = category
                    const isRoleGroup = categoryGroup.label === 'Role'
                    const displayLabel = isRoleGroup
                      ? roleSlugToLabel(value)
                      : value

                    const isSelected = selected.includes(value)
                    return (
                      <li
                        key={value}
                        className="group relative flex items-center align-middle"
                      >
                        <button
                          type="button"
                          aria-pressed={isSelected}
                          aria-label={displayLabel}
                          onClick={() => {
                            onToggle(value)
                            ReactGA.event({
                              category: 'Button Click',
                              action: `User clicked to filter: ${displayLabel}`,
                              label: 'Portfolio Section',
                            })
                          }}
                          className={[
                            'text-sm flex h-auto w-auto cursor-pointer gap-2 rounded-full border px-3 py-1.5 font-medium transition-all duration-200',
                            isSelected
                              ? 'border-brand-600 bg-brand-600 text-white shadow-sm hover:bg-brand-700 dark:border-brand-500 dark:bg-brand-500 dark:hover:bg-brand-600'
                              : 'border-neutral-300 bg-white text-neutral-700 hover:border-neutral-400 hover:bg-neutral-50 dark:border-neutral-600 dark:bg-neutral-800 dark:text-neutral-200 dark:hover:border-neutral-500 dark:hover:bg-neutral-700',
                          ].join(' ')}
                        >
                          <span className="inline-flex items-center gap-2 align-baseline">
                            <span>{displayLabel}</span>
                          </span>
                        </button>
                      </li>
                    )
                  })}
                </ul>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
